/**
 * One row shape for every queued-job list (sync/webhook/ICS update/ICS
 * clean/email) — replaces the former TableSyncRow.js/TableEmbeddingRow.js,
 * which differed only in whether Duration/Auto-login applied and which
 * `job.data` field names the type icon. Driven by props instead of two
 * near-identical components.
 *
 * @param {Object} job
 * @param {number} job.id
 * @param {string} job.created
 * @param {Object} job.data
 * @param {boolean} disabled
 * @param {boolean=false} hasDuration
 * @param {boolean=false} hasAutoLogin
 * @param {string="type"} iconField - job.data field naming the type/provider icon
 * @param {function(Object): string} [accountLabel] - job.data -> account cell label, see columns.js
 * @param {{header: string, render: function(Object): ReactNode}} [secondaryColumn] - see columns.js
 */
import React, { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import Badge from "../../theme/untitled/Badge/Badge";
import Icon from "../../theme/untitled/icons/Icon";
import RowActionsMenu from "../../theme/untitled/RowActionsMenu/RowActionsMenu";
import QueueJobToDuration from "./QueueJobToDuration";
import QueueJobProgress from "./QueueJobProgress";
import { getJobStatus } from "./jobStatus";
import { accountLabelByPlan, emailColumn } from "./columns";
import { store } from "../../store";
import { useAutologinStore } from "../../../../hooks/redux/autologin";
import { useUserStore } from "../../../../hooks/redux/user";
import { useCurrentOrganizationStore } from "../../../../hooks/redux/currentOrganization";
import { useIntegrationsStore } from "../../../../hooks/redux/integrations";
import { useCalendarsStore } from "../../../../hooks/redux/calendars";
import { useDomainsStore } from "../../../../hooks/redux/domains";
import { formatter_ymdhms } from "../../../../helpers/formatters";
import styles from "./QueueJobsPage.module.scss";

const TableRow = ({
  job,
  disabled,
  hasDuration = false,
  hasAutoLogin = false,
  iconField = "type",
  accountLabel = accountLabelByPlan,
  secondaryColumn = emailColumn,
}) => {
  const { id, created, data } = job;

  const { autologinUser } = useAutologinStore();
  const { user } = useUserStore();
  const history = useHistory();
  const { currentOrganization } = useCurrentOrganizationStore();
  const { integrations } = useIntegrationsStore();
  const { calendars } = useCalendarsStore();
  const { domains } = useDomainsStore();

  const onAutoLogin = useCallback(async () => {
    await autologinUser(
      data.email,
      user,
      {
        model: currentOrganization.model,
        subscription: currentOrganization.subscription,
      },
      {
        integrations: integrations.collection,
        domains: domains.collection,
        calendars: calendars.collection,
      }
    );

    if (store.getState().autologin.loginSuccess) {
      history.push("/dashboard");
    }
  }, [
    autologinUser,
    data.email,
    user,
    currentOrganization,
    integrations.collection,
    domains.collection,
    calendars.collection,
    history,
  ]);

  const status = getJobStatus(job);

  const menuOptions = useMemo(
    () =>
      hasAutoLogin
        ? [
            {
              key: "autologin",
              label: "Auto login",
              icon: <Icon name="Key01" size={20} />,
              onClick: onAutoLogin,
            },
          ]
        : [],
    [hasAutoLogin, onAutoLogin]
  );

  const durationCell = hasDuration ? (
    <td>
      <div className={styles.progressCell}>
        <QueueJobToDuration job={job} />
        <QueueJobProgress job={job} />
      </div>
    </td>
  ) : null;

  const actionsCell =
    menuOptions.length > 0 ? (
      <RowActionsMenu items={menuOptions} disabled={disabled} />
    ) : null;

  const accountLabelText = accountLabel(data);
  const secondaryColumnValue = secondaryColumn.render(data);

  return (
    <tr className={styles.row}>
      <td>
        <div className={styles.accountCell}>
          <img
            src={`/img/integrations_icons/${data[iconField]}.png`}
            alt=""
            className={styles.typeIcon}
          />
          <span>{accountLabelText}</span>
        </div>
      </td>
      <td className={styles.idCell}>{id}</td>
      <td className={styles.dateCell}>
        {formatter_ymdhms.format(new Date(created))}
      </td>
      <td>{secondaryColumnValue}</td>
      <td>
        <Badge color={status.color} size="sm" dot>
          {status.label}
        </Badge>
      </td>
      {durationCell}
      <td>{actionsCell}</td>
    </tr>
  );
};

export default TableRow;
